import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { LoadingProvider } from './contexts/LoadingContext';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import DakhlaBot from './components/chatbot/DakhlaBot';
import Home from './pages/Home';
import NotFound from './pages/NotFound';
import ComparePopover from './components/ComparePopover';
import DakhlaButterflyLoader from './components/common/DakhlaButterflyLoader';

import UniversityDetail from './pages/UniversityDetail';
import ComparePage from './pages/ComparePage';
import Login from './pages/Login';
import SignUp from './pages/SignupNew';
import Contact from './pages/Contact';
import Faq from './pages/Faq';
import About from './pages/About';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import DirectApply from './pages/DirectApply';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import TrademarkDisclaimer from './pages/TrademarkDisclaimer';
import BlogList from './pages/BlogList';
import BlogDetail from './pages/BlogDetail';
import VerifyEmail from './pages/VerifyEmail';
import Programs from './pages/Programs';

// Lazy loaded heavy/admin components
const ExamDashboard = lazy(() => import('./pages/ExamDashboard'));
const ExamInterface = lazy(() => import('./pages/ExamInterface'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminSignup = lazy(() => import('./pages/AdminSignup'));

const RouteLoadingFallback = () => <DakhlaButterflyLoader text="Loading..." />;

// Component to conditionally render chatbot
const ConditionalChatbot = () => {
  const { user, isAuthenticated } = useAuth();
  const shouldShowChatbot = isAuthenticated && user?.role === 'student';
  if (!shouldShowChatbot) return null;
  return <DakhlaBot />;
};

const AdminRouteGuard = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const isStudentOrUniversity = isAuthenticated && (user?.role === 'student' || user?.role === 'university');
  if (isStudentOrUniversity) return <NotFound />;
  return children;
};

const SignupRouteGuard = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const isStudentOrUniversity = isAuthenticated && (user?.role === 'student' || user?.role === 'university');
  const isUniversityPartnerSignup = new URLSearchParams(location.search).get('partner') === 'university';
  if (isStudentOrUniversity && isUniversityPartnerSignup) return <NotFound />;
  return <SignUp />;
};

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isStudent, isUniversity } = useAuth();
  const isExamInterfaceRoute = /^\/exam\/[^/]+\/start$/.test(location.pathname);
  const hideNavbar = isExamInterfaceRoute || 
    location.pathname.startsWith('/supad-panel') || 
    location.pathname.startsWith('/admin-signup');
  const hideFooter = hideNavbar || (isAuthenticated && isUniversity);

  useEffect(() => {
    if (isAuthenticated) {
      if (isUniversity) {
        const publicRoutes = ['/', '/about', '/contact', '/faq', '/partners', '/compare', '/profile', '/login', '/signup'];
        const isPublicUniversityRoute = location.pathname.startsWith('/university/') && !location.pathname.startsWith('/university/profile');
        
        if (publicRoutes.includes(location.pathname) || isPublicUniversityRoute) {
          navigate('/dashboard', { replace: true });
        }
      } else if (isStudent) {
        if (location.pathname === '/dashboard') {
          navigate('/profile', { replace: true });
        }
        if (location.pathname === '/login' || location.pathname === '/signup') {
          navigate('/', { replace: true });
        }
      }
    }
  }, [isAuthenticated, isStudent, isUniversity, location.pathname, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col justify-between">
      {!hideNavbar && <Navbar />}
      <main className="flex-1 flex flex-col">
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/partners" element={<NotFound />} />
            <Route path="/exam" element={<NotFound />} />
            <Route path="/exam/:examId/start" element={<NotFound />} />
            <Route path="/university/:id" element={<UniversityDetail />} />
            <Route path="/compare" element={<ComparePage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignupRouteGuard />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/disclaimer" element={<TrademarkDisclaimer />} />
            <Route path="/direct-apply" element={<DirectApply />} />
            <Route path="/dakhla-exam" element={<NotFound />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/dashboard" element={<Profile />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/application-form" element={<NotFound />} />
            <Route path="/my-applications" element={<NotFound />} />
            <Route path="/applications" element={<NotFound />} />
            <Route path="/programs" element={<Programs />} />

            {/* Admin Routes */}
            <Route path="/supad-panel" element={<AdminRouteGuard><AdminLogin /></AdminRouteGuard>} />
            <Route path="/supad-panel/dashboard" element={<AdminRouteGuard><AdminDashboard /></AdminRouteGuard>} />
            <Route path="/admin-signup/:token" element={<AdminRouteGuard><AdminSignup /></AdminRouteGuard>} />

            {/* 404 Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {!hideFooter && <Footer />}
      <ConditionalChatbot />
      {!hideFooter && <ComparePopover />}
    </div>
  );
}

function App() {
  return (
    <LoadingProvider>
      <AuthProvider>
        <ToastProvider>
          <Router>
            <AppContent />
          </Router>
        </ToastProvider>
      </AuthProvider>
    </LoadingProvider>
  );
}

export default App;
